import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BackArrow from '@/components/ui/BackArrow';
import Button from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import { pageContainer, pageItem } from '@/utils/animations';
import { clearStoredTokens, sendVerificationCode } from '@/services/authService';
import { analyticsService } from '@/services/analyticsService';
import { introSlides } from './Welcome';

interface LoginPageProps {
  className?: string;
}

const LoginPage = ({ className }: LoginPageProps) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState('');
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    // The carousel is also used on Welcome. Preload every slide before the
    // first automatic change so the fade never reveals an undecoded image.
    introSlides.forEach(({ image: imageSrc }) => {
      const image = new Image();
      image.decoding = 'async';
      image.src = imageSrc;
    });

    const timer = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % introSlides.length);
    }, 1800);

    return () => window.clearInterval(timer);
  }, []);

  const validatePhoneNumber = (phone: string): { isValid: boolean; error?: string } => {
    const cleaned = phone.replace(/\s|-/g, '');
    
    if (!cleaned) {
      return { isValid: false, error: 'لطفاً شماره موبایل خود را وارد کنید' };
    }
    
    if (!cleaned.startsWith('09')) {
      return { isValid: false, error: 'شماره موبایل باید با 09 شروع شود' };
    }
    
    if (cleaned.length !== 11) {
      return { isValid: false, error: 'شماره موبایل باید 11 رقم باشد' };
    }
    
    if (!/^\d+$/.test(cleaned)) {
      return { isValid: false, error: 'شماره موبایل فقط باید شامل اعداد باشد' };
    }
    
    return { isValid: true };
  };

  const handleLogin = async () => {
    setIsLoading(true);
    setError('');

    try {
      const trimmed = phoneNumber.trim();
      const validation = validatePhoneNumber(trimmed);
      
      if (!validation.isValid) {
        setError(validation.error || 'لطفاً شماره موبایل معتبر وارد کنید');
        return;
      }

      // Send verification code for phone login
      const resp = await sendVerificationCode(trimmed);
      console.log('📱 [Login] sendVerificationCode response:', resp);
      void analyticsService.logEvent('login_phone_submitted');

      // Proceed to OTP verification with phone number
      navigate('/otp-verification', {
        state: {
          phone_number: resp.phone_number || trimmed
        }
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'خطا در ارسال کد. لطفاً دوباره تلاش کنید.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  // تبدیل اعداد انگلیسی به فارسی
  const toPersianDigits = (str: string): string => {
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    return str.replace(/\d/g, (digit) => persianDigits[parseInt(digit)]);
  };

  // تبدیل اعداد فارسی به انگلیسی
  const toEnglishDigits = (str: string): string => {
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    let result = str;
    persianDigits.forEach((persianDigit, index) => {
      result = result.replace(new RegExp(persianDigit, 'g'), index.toString());
    });
    return result;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // تبدیل به انگلیسی و فقط اعداد را قبول کن
    const englishValue = toEnglishDigits(e.target.value);
    const numericValue = englishValue.replace(/[^0-9]/g, '');
    
    // ذخیره به صورت انگلیسی
    setPhoneNumber(numericValue);

    // Clear error when user starts typing
    if (error && numericValue.trim()) {
      setError('');
    }
  };

  const handleBackToWelcome = () => {
    // Remove guest/session leftovers before returning to welcome
    void clearStoredTokens().finally(() => {
      navigate('/welcome', { replace: true });
    });
  };

  const handleSignUp = () => {
    // Same behavior as the back arrow: go back to the landing page without stale auth state
    void clearStoredTokens().finally(() => {
      navigate('/welcome', { replace: true });
    });
  };

  return (
    <motion.div
      className={cn(
        'min-h-screen flex flex-col bg-white',
        'px-6 py-8',
        'relative',
        className
      )}
      variants={pageContainer}
      initial="hidden"
      animate="visible"
      dir="rtl"
    >
      {/* Back arrow in top-right corner (RTL) */}
      <motion.div
        className="absolute top-6 right-5 z-10"
        variants={pageItem}
      >
        <BackArrow
          onClick={handleBackToWelcome}
          className="!p-1.5 [&_svg]:!h-5 [&_svg]:!w-5"
        />
      </motion.div>

      {/* Main content container */}
      <div className="flex-1 flex flex-col justify-start pt-6 w-full max-w-auth-mobile md:max-w-auth-tablet lg:max-w-auth-desktop xl:max-w-auth-desktop-lg mx-auto">
        {/* Smaller version of the Welcome intro */}
        <motion.div className="mb-12 flex w-full flex-col items-center" variants={pageItem}>
          <div className="relative mx-auto flex w-full touch-pan-y items-center justify-center overflow-visible" dir="ltr">
            <div className="mx-auto grid w-full grid-cols-1 justify-items-center">
              {introSlides.map((slide, index) => (
                <div
                  key={slide.image}
                  className={cn(
                    'intro-slide col-start-1 row-start-1 flex w-full max-w-full flex-col items-center justify-self-center text-center transition-opacity duration-[450ms] ease-in-out',
                    activeSlide === index ? 'intro-slide-active pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
                  )}
                  aria-hidden={activeSlide !== index}
                >
                <div className="flex w-full justify-center px-0 text-center">
                  <img
                    src={slide.image}
                    alt={slide.title}
                    className="mb-4 block h-[min(88vw,30.5rem)] w-[min(88vw,30.5rem)] max-h-[43vh] max-w-none shrink-0 object-contain object-center sm:max-h-[49vh]"
                    draggable={false}
                    loading="eager"
                    decoding="async"
                  />
                </div>
                <p dir="rtl" style={{ textAlign: 'center' }} className="mx-auto w-full max-w-md px-3 text-center text-lg font-bold leading-8 text-gray-800 sm:text-xl">
                  {slide.title}
                </p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 flex items-center justify-center gap-2" dir="ltr" aria-label="اسلایدهای معرفی">
            {introSlides.map((slide, index) => (
              <button
                key={slide.image}
                type="button"
                aria-label={`رفتن به اسلاید ${index + 1}`}
                aria-current={activeSlide === index}
                onClick={() => setActiveSlide(index)}
                className={cn(
                  'h-1.5 rounded-full transition-all duration-300',
                  activeSlide === index ? 'w-5 bg-primary-500' : 'w-1.5 bg-gray-300'
                )}
              />
            ))}
          </div>
        </motion.div>

        {/* Phone input */}
        <motion.div
          className="mb-6"
          variants={pageItem}
        >
          <motion.input
            type="tel"
            inputMode="numeric"
            value={toPersianDigits(phoneNumber)}
            onChange={handleInputChange}
            placeholder="شماره همراه"
            maxLength={11}
            className={cn(
              'w-full px-5 py-2.5 rounded-xl border-2 transition-all duration-300',
              'font-persian text-base text-foreground placeholder:text-muted',
              'focus:outline-none focus:ring-2 focus:ring-offset-1',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'text-center bg-soft-white shadow-sm',
              !error && [
                'border-gray-200',
                'hover:border-gray-300 hover:shadow-md',
                'focus:border-gray-400 focus:ring-gray-200'
              ],
              error && [
                'border-red-300 bg-red-50',
                'hover:border-red-400',
                'focus:border-red-500 focus:ring-red-200'
              ]
            )}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          />
          {error && (
            <motion.p
              className="text-red-500 text-sm font-persian text-center mt-2"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {error}
            </motion.p>
          )}
        </motion.div>

        {/* Sign in button */}
        <motion.div
          className="mb-6"
          variants={pageItem}
        >
          <Button
            variant="primary"
            size="md"
            className="w-full rounded-xl"
            onClick={handleLogin}
            disabled={isLoading || phoneNumber.length !== 11}
          >
            {isLoading ? 'در حال ارسال کد...' : 'ارسال کد تایید'}
          </Button>
        </motion.div>

        {/* Sign up link */}
        <motion.div
          className="text-center"
          variants={pageItem}
        >
          <span className="text-gray-500 font-persian text-sm">
            کاربر بوکا نیستی؟{' '}
          </span>
          <button
            type="button"
            onClick={handleSignUp}
            className="text-primary-500 font-persian text-sm font-medium hover:text-primary-600 transition-colors"
          >
            ثبت نام
          </button>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default LoginPage;
